export interface Product {
  id: number;
  name: string;
  nameEn: string;
  category: string;
  categoryId: string;
  tier: 'single' | 'set' | 'premium';
  description: string;
  contents: string;
  servings: string;
  specs: string[];
  price: number;
  unit: string;
  image: string;
  badge?: string;
}

export const categories = [
  { id: 'all', label: '全部商品' },
  { id: 'pork', label: '豬肉' },
  { id: 'beef', label: '牛肉' },
  { id: 'chicken', label: '雞肉' },
  { id: 'lamb', label: '羊肉' },
  { id: 'vegetable', label: '蔬菜' },
];

export const priceTiers = [
  {
    id: 'all',
    label: '全部',
    sublabel: '',
    range: '',
    icon: 'ri-apps-line',
  },
  {
    id: 'single',
    label: '單品',
    sublabel: '基礎肉品',
    range: 'NT$300～800',
    icon: 'ri-leaf-line',
  },
  {
    id: 'set',
    label: '套餐',
    sublabel: '組合肉品',
    range: 'NT$1,000～3,000',
    icon: 'ri-gift-line',
  },
  {
    id: 'premium',
    label: '高端套餐',
    sublabel: '頂級肉品',
    range: 'NT$5,000～20,000',
    icon: 'ri-vip-crown-line',
  },
];

export const products: Product[] = [
  // ─── 單品：豬肉 ───────────────────────────────────────────
  {
    id: 1,
    name: '梅花豬（燒烤切片）',
    nameEn: 'Pork Collar Sliced',
    category: '豬肉',
    categoryId: 'pork',
    tier: 'single',
    description: '梅花肉油花豐富，肉質軟嫩多汁，是燒烤、火鍋的首選部位，每一口都充滿油脂香氣。',
    contents: '梅花豬肉切片 600g',
    servings: '1～2 人份',
    specs: ['規格：600g / 包', '冷凍保存', '產地：台灣'],
    price: 350,
    unit: '包',
    badge: '推薦',
    image: 'https://public.readdy.ai/ai/img_res/8335bab18c822ca177e0ed273f292079.jpg',
  },
  {
    id: 2,
    name: '五花豬（厚切）',
    nameEn: 'Pork Belly Thick Cut',
    category: '豬肉',
    categoryId: 'pork',
    tier: 'single',
    description: '五花肉層次分明，肥瘦相間，厚切保留完整油脂層，適合紅燒、東坡肉或韓式烤肉。',
    contents: '五花豬肉厚切 500g',
    servings: '1～2 人份',
    specs: ['規格：500g / 包', '冷凍保存', '產地：台灣'],
    price: 300,
    unit: '包',
    image: 'https://readdy.ai/api/search-image?query=fresh%20raw%20pork%20belly%20thick%20slices%20showing%20clear%20alternating%20fat%20and%20meat%20layers%20striped%20pattern%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20premium%20quality%20butcher%20presentation&width=400&height=400&seq=pm002&orientation=squarish',
  },
  {
    id: 3,
    name: '里肌豬排',
    nameEn: 'Pork Loin Steak',
    category: '豬肉',
    categoryId: 'pork',
    tier: 'single',
    description: '去骨里肌肉質細嫩，脂肪含量適中，適合煎豬排、炸排骨，是家常料理的百搭食材。',
    contents: '里肌豬排 400g',
    servings: '1 人份',
    specs: ['規格：400g / 包', '冷凍保存', '產地：台灣'],
    price: 300,
    unit: '包',
    image: 'https://readdy.ai/api/search-image?query=fresh%20raw%20pork%20loin%20steak%20boneless%20smooth%20texture%20pink%20color%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20diffused%20lighting%20clean%20minimal%20presentation%20high%20quality%20meat&width=400&height=400&seq=pm003&orientation=squarish',
  },
  // ─── 單品：牛肉 ───────────────────────────────────────────
  {
    id: 4,
    name: '美國肋眼牛排',
    nameEn: 'USDA Ribeye Steak',
    category: '牛肉',
    categoryId: 'beef',
    tier: 'single',
    description: '美國 USDA Choice 肋眼牛排，油花豐富，肉質鮮嫩多汁，是牛排愛好者的首選。',
    contents: '肋眼牛排 300g / 片',
    servings: '1 人份',
    specs: ['規格：300g / 片', '冷凍保存', '產地：美國'],
    price: 600,
    unit: '片',
    badge: '頂級',
    image: 'https://readdy.ai/api/search-image?query=fresh%20raw%20USDA%20ribeye%20beef%20steak%20with%20exceptional%20marbling%20fat%20distribution%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20premium%20gourmet%20supermarket%20style%20high%20resolution&width=400&height=400&seq=bm001&orientation=squarish',
  },
  {
    id: 5,
    name: '澳洲牛小排',
    nameEn: 'Australian Short Rib',
    category: '牛肉',
    categoryId: 'beef',
    tier: 'single',
    description: '澳洲草飼牛小排，肉質鮮甜，油脂豐富，適合燒烤、紅燒或韓式料理。',
    contents: '牛小排 500g',
    servings: '1～2 人份',
    specs: ['規格：500g / 包', '冷凍保存', '產地：澳洲'],
    price: 500,
    unit: '包',
    badge: '熱銷',
    image: 'https://readdy.ai/api/search-image?query=fresh%20raw%20beef%20short%20ribs%20with%20bone%20showing%20marbling%20texture%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20diffused%20lighting%20premium%20quality%20gourmet%20presentation%20Australian%20grass%20fed&width=400&height=400&seq=bm002&orientation=squarish',
  },
  // ─── 單品：雞肉 ───────────────────────────────────────────
  {
    id: 6,
    name: '雞胸肉（去皮）',
    nameEn: 'Chicken Breast Skinless',
    category: '雞肉',
    categoryId: 'chicken',
    tier: 'single',
    description: '新鮮雞胸肉，低脂高蛋白，是健身族群的最愛，適合水煮、煎烤或製作雞肉沙拉。',
    contents: '去皮雞胸肉 500g',
    servings: '1～2 人份',
    specs: ['規格：500g / 包', '冷藏保存', '產地：台灣'],
    price: 300,
    unit: '包',
    image: 'https://readdy.ai/api/search-image?query=fresh%20raw%20skinless%20chicken%20breast%20fillet%20clean%20smooth%20texture%20pale%20pink%20color%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20minimal%20clean%20presentation%20high%20quality%20poultry&width=400&height=400&seq=cm001&orientation=squarish',
  },
  {
    id: 7,
    name: '去骨雞腿排',
    nameEn: 'Boneless Chicken Thigh',
    category: '雞肉',
    categoryId: 'chicken',
    tier: 'single',
    description: '去骨雞腿排，皮脆肉嫩，油脂豐富，適合煎烤、炸雞或日式親子丼。',
    contents: '去骨雞腿排 400g',
    servings: '1～2 人份',
    specs: ['規格：400g / 包', '冷藏保存', '產地：台灣'],
    price: 300,
    unit: '包',
    badge: '熱銷',
    image: 'https://readdy.ai/api/search-image?query=fresh%20raw%20boneless%20chicken%20thigh%20with%20skin%20juicy%20texture%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20diffused%20lighting%20clean%20fresh%20presentation%20high%20quality%20poultry%20product&width=400&height=400&seq=cm002&orientation=squarish',
  },
  // ─── 套餐 ─────────────────────────────────────────────────
  {
    id: 8,
    name: '豬牛雙拼燒烤組',
    nameEn: 'Pork & Beef BBQ Set',
    category: '豬肉',
    categoryId: 'pork',
    tier: 'set',
    description: '精選梅花豬與肋眼牛排的完美組合，一次享受兩種頂級肉品，是燒烤派對的最佳選擇。',
    contents: '梅花豬切片 400g + 肋眼牛排 300g',
    servings: '2～3 人份',
    specs: ['規格：700g / 組', '冷凍保存', '含醃料包'],
    price: 1000,
    unit: '組',
    badge: '熱銷',
    image: 'https://readdy.ai/api/search-image?query=premium%20raw%20meat%20combination%20set%20pork%20collar%20slices%20and%20ribeye%20beef%20steak%20arranged%20beautifully%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20gourmet%20BBQ%20set%20presentation%20high%20resolution&width=400&height=400&seq=set001&orientation=squarish',
  },
  {
    id: 9,
    name: '家庭烤肉套餐',
    nameEn: 'Family BBQ Package',
    category: '豬肉',
    categoryId: 'pork',
    tier: 'set',
    description: '豐盛家庭烤肉套餐，包含多種部位肉品，讓全家人都能盡情享受烤肉的樂趣。',
    contents: '五花豬 400g + 梅花豬 400g + 雞腿排 400g + 牛小排 300g',
    servings: '4～6 人份',
    specs: ['規格：1.5kg / 組', '冷凍保存', '含醃料包 x2'],
    price: 3000,
    unit: '組',
    badge: '推薦',
    image: 'https://readdy.ai/api/search-image?query=premium%20family%20BBQ%20meat%20set%20assorted%20raw%20meats%20pork%20belly%20pork%20collar%20chicken%20thigh%20beef%20short%20ribs%20arranged%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20generous%20portion%20presentation&width=400&height=400&seq=set002&orientation=squarish',
  },
  {
    id: 10,
    name: '精選牛肉雙拼',
    nameEn: 'Premium Beef Duo',
    category: '牛肉',
    categoryId: 'beef',
    tier: 'set',
    description: '肋眼與牛小排的黃金組合，兩種不同口感的頂級牛肉，滿足對牛肉的所有想像。',
    contents: '肋眼牛排 300g + 牛小排 400g',
    servings: '2～3 人份',
    specs: ['規格：700g / 組', '冷凍保存', '附贈醬料'],
    price: 2000,
    unit: '組',
    image: 'https://readdy.ai/api/search-image?query=premium%20raw%20beef%20duo%20set%20ribeye%20steak%20and%20short%20ribs%20arranged%20elegantly%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20premium%20gourmet%20supermarket%20style%20high%20resolution&width=400&height=400&seq=set003&orientation=squarish',
  },
  {
    id: 11,
    name: '健康雞肉輕食組',
    nameEn: 'Healthy Chicken Light Set',
    category: '雞肉',
    categoryId: 'chicken',
    tier: 'set',
    description: '低脂健康組合，雞胸與雞腿的完美搭配，適合注重飲食健康的族群。',
    contents: '雞胸肉 500g + 去骨雞腿排 400g',
    servings: '2～3 人份',
    specs: ['規格：900g / 組', '冷藏保存', '附健康食譜'],
    price: 1000,
    unit: '組',
    image: 'https://readdy.ai/api/search-image?query=fresh%20raw%20healthy%20chicken%20set%20chicken%20breast%20and%20boneless%20thigh%20arranged%20neatly%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20diffused%20lighting%20clean%20minimal%20healthy%20food%20presentation&width=400&height=400&seq=set004&orientation=squarish',
  },
  // ─── 高端套餐 ──────────────────────────────────────────────
  {
    id: 12,
    name: '日本A5和牛套組',
    nameEn: 'Japan A5 Wagyu Set',
    category: '牛肉',
    categoryId: 'beef',
    tier: 'premium',
    description: '日本A5等級和牛，雪花油花密布，入口即化，是世界公認最頂級的牛肉，獻給最挑剔的饕客。',
    contents: '和牛肋眼 200g + 和牛牛小排 200g + 和牛薄切涮涮鍋 300g',
    servings: '2～3 人份',
    specs: ['規格：700g / 組', '冷凍保存', '附和牛認證書'],
    price: 10000,
    unit: '組',
    badge: '頂級',
    image: 'https://readdy.ai/api/search-image?query=Japanese%20A5%20wagyu%20beef%20set%20with%20extraordinary%20snowflake%20marbling%20pattern%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20luxury%20premium%20presentation%20certificate%20included%20high%20resolution%20gourmet&width=400&height=400&seq=prem001&orientation=squarish',
  },
  {
    id: 13,
    name: '伊比利豬頂級套組',
    nameEn: 'Iberico Pork Premium Set',
    category: '豬肉',
    categoryId: 'pork',
    tier: 'premium',
    description: '西班牙伊比利黑豬，以橡果為食，肉質細緻如大理石紋，被譽為「豬肉界的和牛」。',
    contents: '伊比利梅花 300g + 伊比利五花 300g + 伊比利松阪 200g',
    servings: '2～4 人份',
    specs: ['規格：800g / 組', '冷凍保存', '附原產地證明'],
    price: 5000,
    unit: '組',
    badge: '頂級',
    image: 'https://readdy.ai/api/search-image?query=Spanish%20Iberico%20pork%20premium%20set%20with%20fine%20marbling%20acorn%20fed%20black%20pig%20collar%20belly%20and%20jowl%20arranged%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20luxury%20gourmet%20presentation%20high%20resolution&width=400&height=400&seq=prem002&orientation=squarish',
  },
  {
    id: 14,
    name: '頂級聚會豪華套餐',
    nameEn: 'Ultimate Party Feast',
    category: '牛肉',
    categoryId: 'beef',
    tier: 'premium',
    description: '為重要聚會而生的豪華套餐，集結和牛、伊比利豬、紐西蘭羊排，讓每位賓客都驚艷。',
    contents: '和牛肋眼 400g + 伊比利梅花 400g + 紐西蘭羊小排 400g + 頂級雞腿排 400g',
    servings: '6～8 人份',
    specs: ['規格：1.6kg / 組', '冷凍保存', '附精美禮盒包裝'],
    price: 15800,
    unit: '組',
    image: 'https://readdy.ai/api/search-image?query=ultimate%20luxury%20party%20meat%20feast%20set%20wagyu%20beef%20Iberico%20pork%20lamb%20rack%20premium%20chicken%20arranged%20elegantly%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20gift%20box%20presentation%20high%20resolution&width=400&height=400&seq=prem003&orientation=squarish',
  },
  {
    id: 15,
    name: '紐西蘭羊小排套組',
    nameEn: 'NZ Lamb Rack Set',
    category: '羊肉',
    categoryId: 'lamb',
    tier: 'premium',
    description: '紐西蘭草飼羊小排，肉質細嫩，羊膻味低，餐廳級擺盤感，適合煎烤，是高級餐廳常見食材。',
    contents: '羊小排 600g + 羊肩肉 400g',
    servings: '3～4 人份',
    specs: ['規格：1kg / 組', '冷凍保存', '附香草醃料'],
    price: 5000,
    unit: '組',
    badge: '頂級',
    image: 'https://readdy.ai/api/search-image?query=New%20Zealand%20lamb%20rack%20chops%20with%20bone%20frenched%20premium%20cut%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20restaurant%20quality%20presentation%20fine%20dining%20style%20high%20resolution&width=400&height=400&seq=prem004&orientation=squarish',
  },
  // ─── 蔬菜 ──────────────────────────────────────────────────
  {
    id: 17,
    name: '香辣蘿蔔',
    nameEn: 'Spicy Radish',
    category: '蔬菜',
    categoryId: 'vegetable',
    tier: 'single',
    description: '以新鮮白蘿蔔醃製，加入辣椒、蒜末與特調醬汁，口感爽脆，辣味層次豐富，是燒烤、火鍋的絕佳配菜。',
    contents: '香辣蘿蔔 500g',
    servings: '2～3 人份',
    specs: ['規格：500g / 包', '冷藏保存', '產地：台灣'],
    price: 500,
    unit: '包',
    image: 'https://readdy.ai/api/search-image?query=spicy%20pickled%20white%20radish%20slices%20with%20red%20chili%20flakes%20and%20garlic%20in%20a%20clean%20ceramic%20bowl%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20diffused%20lighting%20fresh%20vibrant%20colors%20appetizing%20presentation&width=400&height=400&seq=veg001&orientation=squarish',
  },
  {
    id: 18,
    name: '台式什錦泡菜',
    nameEn: 'Taiwanese Mixed Pickles',
    category: '蔬菜',
    categoryId: 'vegetable',
    tier: 'single',
    description: '精選高麗菜、紅蘿蔔、小黃瓜等多種蔬菜，以台式獨特醃漬工法製作，酸甜爽口，開胃解膩。',
    contents: '台式什錦泡菜 500g',
    servings: '2～3 人份',
    specs: ['規格：500g / 包', '冷藏保存', '產地：台灣'],
    price: 500,
    unit: '包',
    image: 'https://readdy.ai/api/search-image?query=Taiwanese%20mixed%20pickled%20vegetables%20assortment%20cabbage%20carrot%20cucumber%20colorful%20vibrant%20in%20a%20white%20ceramic%20bowl%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20fresh%20appetizing%20presentation&width=400&height=400&seq=veg002&orientation=squarish',
  },
  {
    id: 19,
    name: '黃金泡菜',
    nameEn: 'Golden Kimchi',
    category: '蔬菜',
    categoryId: 'vegetable',
    tier: 'single',
    description: '採用黃金比例醬料醃製，色澤金黃誘人，口感脆嫩，帶有微辣與發酵的獨特風味，是烤肉必備配菜。',
    contents: '黃金泡菜 500g',
    servings: '2～3 人份',
    specs: ['規格：500g / 包', '冷藏保存', '產地：台灣'],
    price: 500,
    unit: '包',
    badge: '推薦',
    image: 'https://readdy.ai/api/search-image?query=golden%20yellow%20kimchi%20fermented%20cabbage%20with%20golden%20sauce%20bright%20yellow%20color%20crispy%20texture%20in%20a%20white%20ceramic%20bowl%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20vibrant%20appetizing&width=400&height=400&seq=veg003&orientation=squarish',
  },
  {
    id: 20,
    name: '醬香條瓜',
    nameEn: 'Soy Sauce Zucchini',
    category: '蔬菜',
    categoryId: 'vegetable',
    tier: 'single',
    description: '精選嫩條瓜以特調醬油醃漬，吸飽醬汁後口感鮮嫩入味，鹹香中帶有自然清甜，是下飯的絕佳小菜。',
    contents: '醬香條瓜 800g',
    servings: '3～4 人份',
    specs: ['規格：800g / 包', '冷藏保存', '產地：台灣'],
    price: 800,
    unit: '包',
    image: 'https://readdy.ai/api/search-image?query=soy%20sauce%20marinated%20zucchini%20strips%20sliced%20tender%20green%20vegetable%20with%20savory%20brown%20soy%20glaze%20in%20a%20white%20ceramic%20bowl%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20diffused%20lighting%20fresh%20clean%20presentation&width=400&height=400&seq=veg004&orientation=squarish',
  },
  {
    id: 21,
    name: '娃娃菜',
    nameEn: 'Baby Cabbage',
    category: '蔬菜',
    categoryId: 'vegetable',
    tier: 'single',
    description: '新鮮嫩娃娃菜，葉片細嫩清甜，適合火鍋、清炒或涮涮鍋，是清爽解膩的天然好食材。',
    contents: '娃娃菜 800g',
    servings: '3～4 人份',
    specs: ['規格：800g / 包', '冷藏保存', '產地：台灣'],
    price: 800,
    unit: '包',
    image: 'https://readdy.ai/api/search-image?query=multiple%20fresh%20baby%20napa%20cabbage%20heads%20bundled%20together%20vibrant%20pale%20green%20and%20yellow%20leaves%20dewy%20fresh%20texture%20arranged%20on%20pure%20white%20background%20top%20view%20professional%20studio%20food%20photography%20bright%20natural%20lighting%20clean%20crisp%20vegetable%20market%20style&width=400&height=400&seq=veg005b&orientation=squarish',
  },
  {
    id: 16,
    name: '和牛燒肉豪華禮盒',
    nameEn: 'Wagyu BBQ Luxury Gift Box',
    category: '牛肉',
    categoryId: 'beef',
    tier: 'premium',
    description: '精選日本A5和牛多個部位，以精美禮盒呈現，是送禮自用兩相宜的頂級選擇。',
    contents: '和牛肋眼 300g + 和牛牛舌 200g + 和牛五花 300g + 和牛骰子牛 200g',
    servings: '4～6 人份',
    specs: ['規格：1kg / 盒', '冷凍保存', '精美禮盒包裝'],
    price: 20000,
    unit: '盒',
    badge: '頂級',
    image: 'https://readdy.ai/api/search-image?query=Japanese%20wagyu%20beef%20luxury%20gift%20box%20set%20multiple%20cuts%20ribeye%20tongue%20belly%20cubed%20steak%20arranged%20in%20premium%20presentation%20on%20pure%20white%20background%20professional%20studio%20food%20photography%20soft%20lighting%20high%20end%20gift%20packaging&width=400&height=400&seq=prem005&orientation=squarish',
  },
];
